import { motion } from "framer-motion";
import { MessageSquare, Shield, HeartHandshake, Check } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Consult",
    subtitle: "Discovery & Assessment",
    description:
      "We begin with an in-depth consultation to understand your property, lifestyle, and security priorities. Every home is unique.",
    details: [
      "Site assessment",
      "Security audit",
      "Requirements analysis",
      "Custom proposal",
    ],
  },
  {
    number: "02",
    icon: Shield,
    title: "Secure",
    subtitle: "Design & Implementation",
    description:
      "Our team designs and implements your bespoke system with minimal disruption to your daily life. Professional installation, zero compromises.",
    details: [
      "System design",
      "Network hardening",
      "Device installation",
      "Integration testing",
    ],
  },
  {
    number: "03",
    icon: HeartHandshake,
    title: "Support",
    subtitle: "Ongoing Partnership",
    description:
      "Your security evolves with you. We provide continuous monitoring, updates, and support to ensure your fortress remains impenetrable.",
    details: [
      "24/7 support",
      "Regular updates",
      "System optimization",
      "Priority response",
    ],
  },
];

export const ProcessTimeline = () => {
  return (
    <section
      id="process"
      className="relative py-24 md:py-32 overflow-hidden bg-[#0a0a0c]"
      data-testid="process-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-50">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#5e5ce6]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#5e5ce6] font-medium">
              Our Process
            </span>
            <div className="h-px w-12 bg-[#5e5ce6]" />
          </div>

          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
            data-testid="process-headline"
          >
            How we work
          </h2>

          <p className="text-base text-white/50">
            A refined three-step process designed for discerning homeowners who
            expect nothing but excellence.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-[100px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5e5ce6]/30 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
                data-testid={`process-step-${index + 1}`}
              >
                {/* Step Number & Icon */}
                <div className="flex flex-col items-center mb-8">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-[#5e5ce6]/10 border border-[#5e5ce6]/20 flex items-center justify-center mb-4">
                      <step.icon className="w-8 h-8 text-[#5e5ce6]" />
                    </div>
                    <span className="absolute -top-2 -right-2 text-xs font-bold text-[#5e5ce6] bg-[#0a0a0c] px-2 py-1 rounded-full border border-[#5e5ce6]/30">
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Content Card */}
                <div className="glass-card rounded-2xl p-6 h-full">
                  <h3 className="text-2xl font-bold text-white mb-1 text-center">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#5e5ce6] font-medium mb-4 text-center">
                    {step.subtitle}
                  </p>
                  <p className="text-sm text-white/50 leading-relaxed mb-6 text-center">
                    {step.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2">
                    {step.details.map((detail) => (
                      <div
                        key={detail}
                        className="flex items-center gap-3 text-sm text-white/60"
                      >
                        <Check className="w-4 h-4 text-[#5e5ce6] shrink-0" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
